import React, { useContext, useState } from "react";
import FormTriggers from "../../../layouts/FormTriggers";
import RadioButtons from "../../../layouts/RadioButtons";
import { axios_instance } from "../../../api/axios_instance";
import FormButtons from "./FormButtons";
import { toast } from "react-toastify";
import {
  buyerDetailsFormValidate,
  checkHoldCase,
  getCompletedStepDetails,
  holdCaseMessage,
} from "../../../utils/detail";
import { DetailFormContext } from "../../../ContextAPI/context";

const BuyerDetailsCheck = ({
  fieldValues,
  stepSelected,
  handleNextStep,
  user,
  isVerificationRequired,
  Message,
  dtsCheckpoints,
  fetchCaseData,
  caseDetails,
}) => {
  const { panelTitle, stepTitle, setDetailFormButtonClicked } =
    useContext(DetailFormContext);
  const { stepDetails, visibilityObj, requiredSubStepFields } = fieldValues;
  const { checklist, btnDraftType } = stepDetails;
  const {
    finance_lead_id,
    id,
    stage,
    sub_stage,
    insurance,
    bank_id,
    is_prepayment_raised,
    cust,
    is_address_match,
    is_signature_match,
    customer_type,
    ownership_transfer,
    step_details,
    maker_id,
  } = caseDetails;

  const initialState = {
    customerName: cust,
    isAddressMatch: is_address_match,
    isSignatureMatch: is_signature_match,
    showCustomerNameError: false,
    showIsAddressMatchError: false,
    showIsSignatureMatchError: false,
  };

  const [state, setState] = useState(initialState);

  // Visibility constants
  const isCustomerNameVisible =
    requiredSubStepFields?.customer_name?.is_visible === 1;
  const isAddressMatchVisible =
    requiredSubStepFields?.address_match?.is_visible === 1;
  const isSignatureMatchVisible =
    requiredSubStepFields?.signature_match?.is_visible === 1 &&
    visibilityObj.is_signature_match;

  // Check if any field requires verification
  const calculatedIsVerificationRequired =
    isCustomerNameVisible || isAddressMatchVisible || isSignatureMatchVisible;

  const saveDetails = async () => {
    const stepDetails = getCompletedStepDetails(
      step_details,
      sub_stage,
      stepSelected
    );
    await axios_instance({
      url: "/dts/dtsAdditionalDetails",
      method: "POST",
      data: {
        finance_lead_id: +finance_lead_id,
        form: stepSelected,
        stage: +stage,
        sub_stage: +sub_stage,
        type: btnDraftType,
        step_details: stepDetails,
        insurance,
        bank_id,
        is_prepayment_raised,
        customer_name: state.customerName,
        is_address_match: state.isAddressMatch,
        is_signature_match: state.isSignatureMatch,
        customer_type,
        ownership_transfer,
        disbursement_id: +id,
      },
    });
    await fetchCaseData();
  };

  const handleSaveAndNext = async () => {
    try {
      const checkHoldStatus = await checkHoldCase(
        finance_lead_id,
        maker_id,
        sub_stage
      );
      toast.dismiss();
      if (checkHoldStatus) {
        return toast.error(holdCaseMessage, { theme: "colored" });
      }
      if (
        !buyerDetailsFormValidate(
          state,
          setState,
          visibilityObj,
          requiredSubStepFields
        )
      ) {
        return toast.error("Please fill all mandatory fields", {
          theme: "colored",
        });
      }
      setDetailFormButtonClicked(true);
      await saveDetails();
      toast(<Message />);
      handleNextStep();
    } catch (error) {
    } finally {
      setDetailFormButtonClicked(false);
    }
  };

  const handleSaveAsDraft = async () => {
    try {
      const checkHoldStatus = await checkHoldCase(
        finance_lead_id,
        maker_id,
        sub_stage
      );
      toast.dismiss();
      if (checkHoldStatus) {
        return toast.error(holdCaseMessage, { theme: "colored" });
      }
      if (
        !buyerDetailsFormValidate(
          state,
          setState,
          visibilityObj,
          requiredSubStepFields
        )
      ) {
        return toast.error("Please fill all mandatory fields", {
          theme: "colored",
        });
      }
      setDetailFormButtonClicked(true);
      await saveDetails();
      toast(<Message />);
    } catch (error) {
    } finally {
      setDetailFormButtonClicked(false);
    }
  };

  const handleInputChange = (inputName, value, errorId) => {
    setState({ ...state, [inputName]: value, [errorId]: false });
  };

  return (
    <>
      <div
        className="table-responsive chklist-box-scroll mCustomScrollbar _mCS_1 mCS-autoHide mCS_no_scrollbar"
        data-mcs-theme="minimal-dark"
      >
        {isVerificationRequired && calculatedIsVerificationRequired ? (
          <div
            id="mCSB_1"
            className="mCustomScrollBox mCS-minimal-dark mCSB_vertical mCSB_outside"
          >
            <div
              id="mCSB_1_container"
              className="mCSB_container mCS_y_hidden mCS_no_scrollbar_y"
            >
              <form id="buyer_details_check">
                <table className="table table-bordered disburse-table">
                  <thead>
                    <tr>
                      <th width="76%" colSpan="2">
                        {panelTitle} - {stepTitle}
                      </th>
                      {checklist && <th width="24%">L1</th>}
                    </tr>
                  </thead>
                  <tbody className="form-table">
                    {isCustomerNameVisible && (
                      <tr>
                        <RadioButtons
                          label={requiredSubStepFields?.customer_name?.name}
                          isRequired={true}
                          className="rto"
                          name="customer_name_doc"
                          disabled={
                            requiredSubStepFields?.customer_name
                              ?.is_editable === 1
                              ? stepDetails.disableMaker
                              : "disabled"
                          }
                          yesChecked={state.customerName === 1 ? true : false}
                          noChecked={state.customerName === 0 ? true : false}
                          onYesClicked={() =>
                            handleInputChange(
                              "customerName",
                              1,
                              "showCustomerNameError"
                            )
                          }
                          onNoClicked={() =>
                            handleInputChange(
                              "customerName",
                              0,
                              "showCustomerNameError"
                            )
                          }
                          showError={state.showCustomerNameError}
                        />
                        {checklist ? (
                          <FormTriggers
                            triggerName="customer_name"
                            triggerValues={dtsCheckpoints}
                            caseDetails={caseDetails}
                            disabled={stepDetails.disableMaker}
                          />
                        ) : (
                          ""
                        )}
                      </tr>
                    )}
                    {isAddressMatchVisible && (
                      <tr>
                        <RadioButtons
                          label={requiredSubStepFields?.address_match?.name}
                          isRequired={true}
                          className="rto"
                          name="is_address_match_doc"
                          disabled={
                            requiredSubStepFields?.address_match
                              ?.is_editable === 1
                              ? stepDetails.disableMaker
                              : "disabled"
                          }
                          yesChecked={state.isAddressMatch === 1 ? true : false}
                          noChecked={state.isAddressMatch === 0 ? true : false}
                          onYesClicked={() =>
                            handleInputChange(
                              "isAddressMatch",
                              1,
                              "showIsAddressMatchError"
                            )
                          }
                          onNoClicked={() =>
                            handleInputChange(
                              "isAddressMatch",
                              0,
                              "showIsAddressMatchError"
                            )
                          }
                          showError={state.showIsAddressMatchError}
                        />
                        {checklist ? (
                          <FormTriggers
                            triggerName="is_address_match"
                            triggerValues={dtsCheckpoints}
                            caseDetails={caseDetails}
                            disabled={stepDetails.disableMaker}
                          />
                        ) : (
                          ""
                        )}
                      </tr>
                    )}
                    {isSignatureMatchVisible && (
                      <tr>
                        <RadioButtons
                          label={requiredSubStepFields?.signature_match?.name}
                          isRequired={true}
                          className="rto"
                          name="is_signature_match"
                          disabled={
                            requiredSubStepFields?.signature_match
                              ?.is_editable === 1
                              ? stepDetails.disableMaker
                              : "disabled"
                          }
                          yesChecked={
                            state.isSignatureMatch === 1 ? true : false
                          }
                          noChecked={
                            state.isSignatureMatch === 0 ? true : false
                          }
                          onYesClicked={() =>
                            handleInputChange(
                              "isSignatureMatch",
                              1,
                              "showIsSignatureMatchError"
                            )
                          }
                          onNoClicked={() =>
                            handleInputChange(
                              "isSignatureMatch",
                              0,
                              "showIsSignatureMatchError"
                            )
                          }
                          showError={state.showIsSignatureMatchError}
                        />
                        {checklist ? (
                          <FormTriggers
                            triggerName="is_signature_match"
                            triggerValues={dtsCheckpoints}
                            caseDetails={caseDetails}
                            disabled={stepDetails.disableMaker}
                          />
                        ) : (
                          ""
                        )}
                      </tr>
                    )}
                  </tbody>
                </table>
              </form>
            </div>
          </div>
        ) : (
          <div className="disburse-table no-form">
            There are no verification required in this section. Please click
            Next/Skip to proceed
          </div>
        )}
      </div>
      <FormButtons
        caseDetails={caseDetails}
        user={user}
        stepDetails={stepDetails}
        handleNextButtonClick={handleNextStep}
        handleSaveAndNext={handleSaveAndNext}
        handleSaveAsDraft={handleSaveAsDraft}
        isVerificationRequired={calculatedIsVerificationRequired}
        isReadOnlyUser={fieldValues.isReadOnlyUser}
      />
    </>
  );
};

export default BuyerDetailsCheck;
